// Step 6 - read back everything, then start.
//
// Every value on this page is read from `detail`, never from a step's local
// form: the point of the summary is to show what the SERVER holds, because that
// is what the processor will act on. An Edit link goes back to the step that
// owns the value rather than editing it in place, so there is exactly one form
// per field in this wizard.
//
// Start is the irreversible click. It resolves the tags against the live
// workspace, flips the run to `running` under a compare-and-set, and queues one
// job per mailbox; from then on the run is out of the wizard for good.

import { useState } from 'react';

import {
  sqApi,
  sqErrorCode,
  sqErrorMessage,
  type ApiError,
  type SqBisonParams,
  type SqInstantlyParams,
  type SqPlusVibeParams,
  type SqSmartleadParams,
} from '../sequencerApi';
import type { SqStepProps } from './shared';

/** The wizard pieces the server says are still missing, off a NOT_READY. Already
 *  phrased for display by the router. */
function missingOf(err: unknown): string[] | null {
  const data = (err as ApiError).data as { missing?: unknown } | undefined;
  if (!data || !Array.isArray(data.missing)) return null;
  return data.missing.filter((m): m is string => typeof m === 'string');
}

/** Every Smartlead param, in the order the parameters step lays them out.
 *  `trackingDomain` is the first account's real domain (or a placeholder), so
 *  the tracking read-back shows the same subdomain the step previewed. */
function smartleadParamRows(
  p: SqSmartleadParams,
  trackingDomain: string,
): { label: string; value: string }[] {
  return [
    { label: 'Daily limit', value: String(p.max_email_per_day) },
    { label: 'Sending gap', value: `${p.time_to_wait_in_mins} min` },
    { label: 'Warmup', value: p.enable_warmup },
    { label: 'Warmup daily total', value: String(p.total_warmup_per_day) },
    { label: 'Warmup ramp-up', value: p.enable_warmup_rampup },
    { label: 'Daily rampup step', value: String(p.daily_rampup) },
    { label: 'Reply rate', value: `${p.reply_rate_pct}%` },
    {
      label: 'Tracking',
      value: p.tracking.enabled ? `on - ${p.tracking.prefix}.${trackingDomain}` : 'off',
    },
  ];
}

/** Every PlusVibe param, in the order the parameters step lays them out. */
function plusVibeParamRows(p: SqPlusVibeParams): { label: string; value: string }[] {
  return [
    { label: 'Minimum interval', value: `${p.min_interval} min` },
    { label: 'Daily limit', value: String(p.daily_limit) },
    { label: 'Campaign ramp-up', value: p.enable_camp_rampup },
    { label: 'Campaign ramp-up start', value: String(p.camp_ramup_start) },
    { label: 'Campaign ramp-up increment', value: String(p.camp_rampup_increment) },
    { label: 'Warmup', value: p.enable_warmup },
    { label: 'Warmup daily limit', value: String(p.warmup_daily_limit) },
    { label: 'Warmup ramp-up', value: p.enable_warmup_rampup },
    { label: 'Warmup ramp-up start', value: String(p.warmup_rampup_start) },
    { label: 'Warmup ramp-up increment', value: String(p.warmup_rampup_increment) },
    { label: 'Reply rate', value: `${p.reply_rate_pct}%` },
  ];
}

/** Every Instantly param, in the order StepParamsInstantly lays them out. */
function instantlyParamRows(p: SqInstantlyParams): { label: string; value: string }[] {
  return [
    { label: 'Daily limit', value: String(p.daily_limit) },
    { label: 'Sending gap', value: `${p.sending_gap} min` },
    { label: 'Slow ramp', value: p.enable_slow_ramp ? 'yes' : 'no' },
    { label: 'Warmup', value: p.enable_warmup },
    { label: 'Warmup daily limit', value: String(p.warmup_limit) },
    { label: 'Warmup increment', value: String(p.warmup_increment) },
    { label: 'Warmup reply rate', value: `${p.reply_rate_pct}%` },
    // A run saved before the field existed has no ftag at all - shown as none
    // rather than an empty cell, because "no ftag" is a real configuration.
    { label: 'Warmup custom ftag', value: p.warmup_custom_ftag || 'none' },
    { label: 'Open rate', value: `${p.open_rate}%` },
    { label: 'Spam protection', value: `${p.spam_save_rate}%` },
    { label: 'Mark important', value: `${p.important_rate}%` },
    { label: 'Weekdays only', value: p.weekday_only ? 'yes' : 'no' },
    { label: 'Read emulation', value: p.read_emulation ? 'yes' : 'no' },
    {
      label: 'Custom tracking domain',
      value: p.include_tracking ? 'include (inst. CNAME auto-created)' : 'exclude',
    },
    { label: 'Warm tracking domain', value: p.warm_ctd ? 'yes' : 'no' },
  ];
}

/** Every Bison param, in the order StepParamsBison lays them out. The list is
 *  short because the platform is: Bison ramps warmup itself. */
function bisonParamRows(p: SqBisonParams): { label: string; value: string }[] {
  return [
    { label: 'Daily limit', value: String(p.daily_limit) },
    { label: 'Warmup', value: p.enable_warmup },
    { label: 'Warmup daily limit', value: String(p.warmup_daily_limit) },
  ];
}

function EditLink({
  step,
  onGo,
  disabled,
}: {
  step: number;
  onGo: (step: number) => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      className="sq-btn sq-btn--ghost sq-btn--small"
      onClick={() => onGo(step)}
      disabled={disabled}
    >
      Edit
    </button>
  );
}

export function StepSummary({
  runId,
  detail,
  reload,
  notifyEnqueued,
  goToStep,
  toast,
}: SqStepProps): JSX.Element {
  const { run, items } = detail;
  // Connect only: no workspace, parameters or tags exist to read back.
  const connectOnly = run.sequencer === 'warmuptool';

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<string[] | null>(null);

  // The Smartlead tracking subdomain is the prefix joined to each mailbox's OWN
  // domain, so the read-back is built off the first account - the same computation
  // StepParams previews with, and a placeholder when there are no accounts yet.
  const firstEmail = items[0]?.email ?? '';
  const at = firstEmail.lastIndexOf('@');
  const firstDomain = at >= 0 ? firstEmail.slice(at + 1) : '';
  const trackingDomain = firstDomain || 'your-domain.com';

  async function start(): Promise<void> {
    setBusy(true);
    setError(null);
    setMissing(null);
    try {
      const result = await sqApi.start(runId);
      // The jobs are on the queue, not yet in a worker's hands. Tell the shell
      // to keep polling across that gap, or the progress view the operator is
      // about to land on sits still on a row of `pending`.
      notifyEnqueued();
      if (result.droppedTagIds.length > 0) {
        // Not a failure, but the operator picked those tags on purpose and they
        // are not being applied: they no longer exist in the workspace, and
        // assigning a dead id fails the whole config push for an account.
        toast(
          'warn',
          `${result.droppedTagIds.length} picked tag(s) no longer exist in the workspace and were dropped. Everything else was queued.`,
        );
      } else {
        toast('success', `Started. ${result.accountsQueued} mailbox(es) queued.`);
      }
      await reload();
    } catch (err) {
      const code = sqErrorCode(err);
      if (code === 'ALREADY_STARTED') {
        // Someone else's click won, or this one was double-fired. Nothing was
        // queued twice and the run is already moving, so the honest response is
        // to show the operator the run as it now is.
        await reload();
        return;
      }
      if (code === 'NOT_READY') setMissing(missingOf(err));
      setError(sqErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const go = (step: number): void => void goToStep(step);

  return (
    <section className="sq-step">
      <div className="sq-step-head">
        <h2>Step {connectOnly ? 4 : 6} - Summary</h2>
        <p>
          {connectOnly
            ? 'Everything below is what the server holds for this run. Starting connects each mailbox by OAuth and records what its session reports. Nothing else is configured: warmup and limits stay at the instance defaults.'
            : 'Everything below is what the server holds for this run. Starting connects each mailbox by OAuth, pushes the profile and the tags, and then QA verifies the result against the live workspace.'}
        </p>
      </div>
      <div className="sq-step-body">
        <div className="sq-sumcard">
          <div className="sq-sumcard-head">
            <h3>Sheet and sequencer</h3>
            <EditLink step={1} onGo={go} disabled={busy} />
          </div>
          <dl className="sq-sumlist">
            <div>
              <dt>Google Sheet</dt>
              <dd className="sq-mono">{run.name}</dd>
            </div>
            <div>
              <dt>Sequencer</dt>
              <dd>{run.sequencer}</dd>
            </div>
          </dl>
          <span className="sq-hint">
            The sheet name is the run's label, fixed when the run was created. Edit goes to the
            sequencer step, which is the only editable half of this card.
          </span>
        </div>

        <div className="sq-sumcard">
          <div className="sq-sumcard-head">
            <h3>Destination</h3>
            <EditLink step={connectOnly ? 2 : 4} onGo={go} disabled={busy} />
          </div>
          {run.sequencer === 'smartlead' ? (
            <dl className="sq-sumlist">
              <div>
                <dt>Workspace user id</dt>
                <dd className="sq-mono">{run.sl_user_id ?? 'not set'}</dd>
              </div>
              <div>
                <dt>API key</dt>
                <dd>{run.has_api_key ? 'held and validated' : 'not held'}</dd>
              </div>
            </dl>
          ) : connectOnly ? (
            <dl className="sq-sumlist">
              <div>
                <dt>API URL</dt>
                <dd className="sq-mono">{run.base_url ?? 'not set'}</dd>
              </div>
              <div>
                <dt>Warmuptool API key</dt>
                <dd>{run.has_api_key ? 'held and validated' : 'not held'}</dd>
              </div>
            </dl>
          ) : (
            <dl className="sq-sumlist">
              <div>
                <dt>Workspace</dt>
                <dd>{run.workspace_name ?? 'not picked'}</dd>
              </div>
              {run.sequencer === 'bison' && (
                <div>
                  <dt>Instance URL</dt>
                  <dd className="sq-mono">{run.base_url ?? 'not set'}</dd>
                </div>
              )}
              <div>
                <dt>
                  {run.sequencer === 'instantly'
                    ? 'Instantly API key'
                    : run.sequencer === 'bison'
                      ? 'Bison API key'
                      : 'API key'}
                </dt>
                <dd>{run.has_api_key ? 'held and validated' : 'not held'}</dd>
              </div>
            </dl>
          )}
        </div>

        <div className="sq-sumcard">
          <div className="sq-sumcard-head">
            <h3>Accounts</h3>
            <EditLink step={3} onGo={go} disabled={busy} />
          </div>
          <dl className="sq-sumlist">
            <div>
              <dt>Mailboxes</dt>
              <dd>
                <strong>{items.length}</strong>
              </dd>
            </div>
            <div>
              <dt>Passwords</dt>
              <dd>
                {run.has_item_passwords
                  ? 'held for this run'
                  : 'purged - paste the sheet again'}
              </dd>
            </div>
          </dl>
        </div>

        {!connectOnly && (
          <>
            <div className="sq-sumcard">
              <div className="sq-sumcard-head">
                <h3>Parameters</h3>
                <EditLink step={5} onGo={go} disabled={busy} />
              </div>
              {run.params === null ? (
                <div className="sq-notice sq-notice--warn">
                  No parameters are saved for this run yet. Open step 5 and save them.
                </div>
              ) : (
                <dl className="sq-sumlist">
                  {(run.sequencer === 'instantly'
                    ? instantlyParamRows(run.params as SqInstantlyParams)
                    : run.sequencer === 'bison'
                      ? bisonParamRows(run.params as SqBisonParams)
                      : run.sequencer === 'smartlead'
                        ? smartleadParamRows(run.params as SqSmartleadParams, trackingDomain)
                      : plusVibeParamRows(run.params as SqPlusVibeParams)
                  ).map((r) => (
                    <div key={r.label}>
                      <dt>{r.label}</dt>
                      <dd className="sq-mono">{r.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>

            <div className="sq-sumcard">
              <div className="sq-sumcard-head">
                <h3>Tags</h3>
                <EditLink step={5} onGo={go} disabled={busy} />
              </div>
              <dl className="sq-sumlist">
                <div>
                  <dt>Standardized tag</dt>
                  <dd className="sq-mono">{run.tag_name}</dd>
                </div>
                <div>
                  <dt>Existing tags picked</dt>
                  <dd>{run.params?.extra_tags.existing_ids.length ?? 0}</dd>
                </div>
                <div>
                  <dt>New tags to create</dt>
                  <dd>
                    {(run.params?.extra_tags.new_names.length ?? 0) === 0
                      ? 'none'
                      : run.params?.extra_tags.new_names.join(', ')}
                  </dd>
                </div>
              </dl>
              <span className="sq-hint">
                {run.tag_name} is created in the workspace at start if it is not there already, and
                reused if it is. Picked tags are re-checked against the live workspace at the same
                moment; any that have since been deleted are dropped rather than assigned.
              </span>
            </div>
          </>
        )}

        {connectOnly && (
          <div className="sq-notice">
            <strong>Before you start:</strong> this instance's Google OAuth client has to be usable
            on each panel (Security {'->'} API Controls {'->'} Manage App Access). It is not in the
            standard trusted list panel config applies. The walk clicks only Continue and Allow, so
            a blocked or unverified app, or a screen with permission checkboxes, ends as a failed
            or pending session rather than a connected mailbox.
          </div>
        )}

        {run.sequencer === 'smartlead' && (
          <div className="sq-notice">
            <strong>Before you start:</strong> Smartlead's OAuth client must be marked Trusted in
            each panel's Google Workspace admin (Security {'->'} API Controls {'->'} Manage App
            Access). If these panels have never connected through Smartlead, run OAuth Trust Runs
            with Smartlead's client id first.
          </div>
        )}

        {error && (
          <div className="sq-notice sq-notice--err">
            <div>{error}</div>
            {missing !== null && missing.length > 0 && (
              <ul className="sq-list">
                {missing.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="sq-actions">
          <button
            type="button"
            className="sq-btn sq-btn--primary sq-btn--big"
            onClick={() => void start()}
            disabled={busy}
          >
            {busy ? 'Starting...' : connectOnly ? 'Start Connect' : 'Start Connect & Config'}
          </button>
        </div>
        <span className="sq-hint">
          Starting queues one job per mailbox and closes this wizard - the run cannot be edited
          afterwards. Individual mailboxes can still be retried from the run's progress view.
        </span>
      </div>
    </section>
  );
}
